import React from "react";
import { useTranslation } from "react-i18next";
import { CellValue, readSheet, Row } from "read-excel-file/browser";

import FailedView from "components/import-file-dialog/FailedView";
import ImportFileDialog, { DialogState } from "components/import-file-dialog/ImportFileDialog";
import ImportKeysFailedDialog from "components/import-file-dialog/ImportKeysFailedDialog";
import LoadingView from "components/import-file-dialog/LoadingView";
import SelectingView from "components/import-file-dialog/SelectingView";
import { MANDATORY_ASSET_FIELDS } from "domain/asset-management/assetPathMapping";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";

interface Props {
    onViewAssets: (assets: Record<string, string>[], file: File | undefined) => void;
}
const MAX_ALLOWED_ROWS = 9_000;

export function UploadAssetsBehavior(props: Props) {
    const { t } = useTranslation();
    const [dialogState, setDialogState] = React.useState(DialogState.SELECTING_FILE);

    const [error, setError] = React.useState<string>();
    const [assets, setAssets] = React.useState<Record<string, string>[]>([]);
    const [failedAssets, setFailedAssets] = React.useState<Record<string, string>[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const selectedFileReference = React.useRef<File | undefined>();

    const parseCSV = (result: string) => {
        setDialogState(DialogState.LOADING_FILE);
        const lines = result.split(/\r\n|\r|\n/);
        const headers = lines[0].split(",").map((header) => header.trim());

        const parsedData: Record<string, string>[] = [];
        if (lines.length <= 1) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setAssets([]);
            setFailedAssets([]);
            setError(t("AssetManagement.upload.fileUploadModal.error.emptyFile"));
            throw new Error(t("AssetManagement.upload.fileUploadModal.error.emptyFile"));
        }

        if (lines.length > MAX_ALLOWED_ROWS) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setAssets([]);
            setFailedAssets([]);
            setError(t("AssetManagement.upload.fileUploadModal.error.limitExceeded", { maxAllowed: MAX_ALLOWED_ROWS }));
            throw new Error(
                t("AssetManagement.upload.fileUploadModal.error.limitExceeded", { maxAllowed: MAX_ALLOWED_ROWS })
            );
        }

        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();

            if (!line) {
                break;
            }
            const isEmptyRow = !line || line.split(",").every((cell) => !cell.trim());
            if (isEmptyRow) {
                continue;
            }

            const values = line.split(",").map((value) => value.trim());

            const rowData: Record<string, string> = {};
            headers.forEach((header, index) => {
                rowData[header] = values[index] || "";
            });

            parsedData.push(rowData);
        }

        setFailedAssets([]);
        setAssets(parsedData);
    };

    const parseXlsx = async (file: File) => {
        setDialogState(DialogState.LOADING_FILE);
        const rows = await readSheet(file);

        if (rows.length === 0) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setAssets([]);
            setFailedAssets([]);
            setError(t("AssetManagement.upload.fileUploadModal.error.emptyFile"));
            throw new Error(t("AssetManagement.upload.fileUploadModal.error.emptyFile"));
        }

        const dataRows = rows.slice(1);

        if (dataRows.length > MAX_ALLOWED_ROWS) {
            setDialogState(DialogState.IMPORTING_FAILED);
            setAssets([]);
            setFailedAssets([]);
            setError(t("AssetManagement.upload.fileUploadModal.error.limitExceeded", { maxAllowed: MAX_ALLOWED_ROWS }));
            throw new Error(
                t("AssetManagement.upload.fileUploadModal.error.limitExceeded", { maxAllowed: MAX_ALLOWED_ROWS })
            );
        }

        const headers = rows[0].map((header: CellValue | null) => String(header ?? ""));
        const parsedData: Record<string, string>[] = dataRows.map((row: Row) => {
            const record: Record<string, string> = {};
            headers.forEach((header: string, index: number) => {
                record[header] = String(row[index] ?? "");
            });
            return record;
        });

        setFailedAssets([]);
        setAssets(parsedData);
    };

    const handleFileUpload = (file: File) => {
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith(".xlsx")) {
            parseXlsx(file).catch(() => {
                setDialogState(DialogState.IMPORTING_FAILED);
            });
            return;
        } else if (fileName.endsWith(".csv")) {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = () => {
                try {
                    parseCSV(reader.result as string);
                } catch {
                    setDialogState(DialogState.IMPORTING_FAILED);
                }
            };
            return;
        } else {
            setAssets([]);
            setFailedAssets([]);
            setError(t("AssetManagement.upload.fileUploadModal.error.invalidFileType"));
            setDialogState(DialogState.IMPORTING_FAILED);
        }
    };

    React.useEffect(() => {
        if (assets.length > 0 || failedAssets.length > 0) {
            setDialogState(DialogState.VIEWING_ASSETS);
        }
    }, [failedAssets, assets]);

    const createDialogLogic = React.useCallback(() => {
        switch (dialogState) {
            case DialogState.SELECTING_FILE:
                setError(undefined);
                return;

            case DialogState.LOADING_FILE: {
                if (selectedFileReference.current === undefined) {
                    setError(t("Common.noFileSelected"));
                    return;
                }
                setError(undefined);
                handleFileUpload(selectedFileReference.current);
                return;
            }
            case DialogState.VIEWING_ASSETS: {
                props.onViewAssets(assets, selectedFileReference.current);
                return;
            }
        }
    }, [dialogState]);

    function onDownloadFileClicked() {
        const csvContent = [MANDATORY_ASSET_FIELDS.join(",")].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "assets_template.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    const dispatch = () => {
        if (selectedFileReference.current === undefined) {
            setDialogState(DialogState.SELECTING_FILE);
        } else {
            setDialogState(DialogState.LOADING_FILE);
        }
    };

    const reset = () => {
        selectedFileReference.current = undefined;
        setAssets([]);
        setError(undefined);
        setDialogState(DialogState.SELECTING_FILE);
    };
    const handleFileDrop = (fileList: FileList, event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        usageStatisticsService.sendEvent({
            category: Category.ASSET_MANAGEMENT,
            action: Action.UPLOAD_ASSETS_FILE_DRAGGING,
        });
        if (fileList.length > 0) {
            selectedFileReference.current = Array.from(fileList)[0];
        }
        dispatch();
    };

    function onFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { files } = event.target;
        usageStatisticsService.sendEvent({
            category: Category.ASSET_MANAGEMENT,
            action: Action.UPLOAD_ASSETS_FILE_SELECTING,
        });
        if (files !== null) {
            selectedFileReference.current = Array.from(files)[0];
        }
        dispatch();
    }

    function onSelectFileClicked() {
        fileInputRef.current?.click();
    }

    const loadingFile = <LoadingView loadingMessage={t("AssetManagement.upload.fileUploadModal.uploading")} />;
    const loadingFailed = (
        <FailedView
            failureMessage={typeof error === "string" ? t(error) : t("Common.failedToLoadFile")}
            onUploadAnotherClicked={reset}
            onClose={() => props.onViewAssets(assets, selectedFileReference.current)}
        />
    );

    const selecting = (
        <SelectingView
            handleFileDrop={handleFileDrop}
            onFileInputChange={onFileInputChange}
            onSelectFileClicked={onSelectFileClicked}
            fileInputRef={fileInputRef}
            licenseKeysUpload={true}
            onDownloadFileClicked={onDownloadFileClicked}
        />
    );

    const importingFailedErrorMessage =
        error !== undefined
            ? t("AssetManagement.upload.fileUploadModal.error.importFailed", { error })
            : t("AssetManagement.upload.fileUploadModal.error.failedUploadMessage");

    const importingFailed = (
        <ImportKeysFailedDialog
            failureMessage={importingFailedErrorMessage}
            onUploadAnotherClicked={reset}
            onClose={() => props.onViewAssets([], undefined)}
            licenseKeysUpload={true}
            onDownloadFileClicked={onDownloadFileClicked}
        />
    );
    const stateToContent = new Map<DialogState, JSX.Element>([
        [DialogState.SELECTING_FILE, selecting],
        [DialogState.LOADING_FILE, loadingFile],
        [DialogState.LOADING_FILE_FAILED, loadingFailed],
        [DialogState.IMPORTING_FAILED, importingFailed],
        [DialogState.IMPORTING_SUCCEEDED, <></>],
        [DialogState.VIEWING_ASSETS, <></>],
        [DialogState.IMPORTING_ASSETS, loadingFile],
    ]);
    return (
        <ImportFileDialog
            onClose={() => props.onViewAssets(assets, selectedFileReference.current)}
            dialogLogic={createDialogLogic}
            stateToContent={stateToContent}
            stateDispatch={dispatch}
            dialogState={dialogState}
        />
    );
}
